import { IntlMessageFormat } from 'intl-messageformat';
import { formattingLocale } from './formatting_locale';
import type { Locale, TranslateParams } from './types';

/**
 * Cache of parsed IntlMessageFormat instances, keyed by `locale::message`.
 * Avoids re-parsing the same ICU template on repeated calls. A cached value
 * of `null` means parsing this message previously failed (malformed ICU
 * syntax) — this stops a permanently-bad string from re-parsing (and
 * re-warning) on every single call, e.g. every render or keystroke.
 */
const cache = new Map<string, IntlMessageFormat | null>();

/**
 * Format an ICU message string with the given locale and optional params.
 *
 * Fast path: if `params` is undefined AND the message contains no `{`,
 * the string is returned as-is without touching the ICU parser.
 */
export function formatMessage(
  message: string,
  locale: Locale,
  params?: TranslateParams,
): string {
  // Fast path — no ICU syntax, no params
  if (params === undefined && !message.includes('{')) {
    return message;
  }

  const cacheKey = `${locale}::${message}`;
  let parsed = cache.get(cacheKey);
  if (parsed === null) {
    // A previous call already found this message unparseable — don't retry.
    return message;
  }
  if (parsed === undefined) {
    // Catch malformed ICU argument syntax (e.g. a translation string that
    // contains a raw `{...}` that isn't valid ICU, such as `{date:YYYY}`
    // instead of `{date, date, YYYY}`). A bad translation string must never
    // crash the page it's rendered on — fall back to the raw message.
    try {
      parsed = new IntlMessageFormat(message, formattingLocale(locale));
    } catch (err) {
      console.warn('[i18n] formatMessage parse error for key:', message, err);
      cache.set(cacheKey, null);
      return message;
    }
    cache.set(cacheKey, parsed);
  }

  // IntlMessageFormat.format() accepts Record<string, unknown>
  // Catch MissingValueError: if a placeholder is present but no value was provided,
  // return the unformatted message rather than throwing at the call site.
  let result: ReturnType<IntlMessageFormat['format']>;
  try {
    result = parsed.format(params ?? {});
  } catch (err) {
    // Missing ICU variable — log and fall back to the raw message string
    console.warn('[i18n] formatMessage error for key:', message, err);
    return message;
  }

  // format() can return string or React nodes; we always want a string here
  return typeof result === 'string' ? result : String(result);
}
